import React, { useEffect, useState } from 'react';
import { RotateCcw, Save } from 'lucide-react';
import {
  SERVICE_CATALOG,
  type OpsEnvironment,
  type OpsRuntime,
  type ServiceId,
  type ServicePathsMap,
} from './opsConsoleData';
import { loadPaths, resetPaths, savePaths } from './opsConsoleStorage';
import { PanelCard, PrimaryButton, TextInput } from './components';

export default function PathsPanel({
  env,
  runtime,
}: {
  env: OpsEnvironment;
  runtime: OpsRuntime;
}) {
  const [paths, setPaths] = useState<ServicePathsMap>(() => loadPaths(env, runtime));
  const [savedAt, setSavedAt] = useState<string | null>(null);

  useEffect(() => {
    setPaths(loadPaths(env, runtime));
    setSavedAt(null);
  }, [env, runtime]);

  const isPathMode = runtime === 'no-docker';
  const title = isPathMode ? 'Local project paths' : 'Service URLs';
  const subtitle = isPathMode
    ? `Filesystem roots for ${env} · no-docker`
    : `Endpoints for ${env} · ${runtime}`;

  const update = (id: ServiceId, value: string) => {
    setPaths((prev) => ({ ...prev, [id]: value }));
    setSavedAt(null);
  };

  const onSave = () => {
    savePaths(env, runtime, paths);
    setSavedAt(new Date().toLocaleTimeString());
  };

  const onReset = () => {
    setPaths(resetPaths(env, runtime));
    setSavedAt(null);
  };

  return (
    <PanelCard
      title={title}
      subtitle={subtitle}
      action={
        <div className="flex items-center gap-1.5">
          {savedAt && <span className="text-[10px] text-emerald-600">Saved {savedAt}</span>}
          <PrimaryButton variant="secondary" onClick={onReset}>
            <RotateCcw size={12} /> Reset
          </PrimaryButton>
          <PrimaryButton onClick={onSave}>
            <Save size={12} /> Save
          </PrimaryButton>
        </div>
      }
    >
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {SERVICE_CATALOG.map((svc) => (
          <TextInput
            key={svc.id}
            label={svc.name}
            value={paths[svc.id] || ''}
            onChange={(v) => update(svc.id, v)}
            mono
          />
        ))}
      </div>
    </PanelCard>
  );
}
